"use client";

import { cx } from "class-variance-authority";
import { usePathname, useRouter } from "next/navigation";
import { type ComponentProps, useRef } from "react";
import {
	Menu as AriaMenu,
	MenuItem as AriaMenuItem,
	Button,
	Header,
	MenuSection,
	MenuTrigger,
	Popover,
	SubmenuTrigger,
} from "react-aria-components";

import { useAuth } from "@/components/auth";
import { Chevron } from "@/components/icons/chevron";
import { Deployments } from "@/components/icons/deployments";
import { Dot } from "@/components/icons/dot";
import { Home } from "@/components/icons/home";
import { Logout } from "@/components/icons/logout";
import { Policies } from "@/components/icons/policies";
import { Profile } from "@/components/icons/profile";

import { Protections } from "@/components/icons/protections";
import { Settings } from "@/components/icons/settings";

function MenuItem(props: ComponentProps<typeof AriaMenuItem>) {
	const [, activeSegment] = usePathname().split("/");
	const [, segment] = props.href?.toString().split("/") ?? [];
	const active = segment === activeSegment;

	return (
		<AriaMenuItem
			{...props}
			data-active={active}
			className={cx(
				"flex px-3 items-center h-9 gap-1.5 rounded-full transition-all w-full",
				"data-[active=true]:inset-shadow-glow/20 data-[active=true]:outline data-[active=true]:bg-background",
				"not-data-[active=true]:[&_svg]:text-muted-foreground",
			)}
		/>
	);
}

export function Menu({ email }: { email: string }) {
	const router = useRouter();
	const submenuTriggerRef = useRef(null);
	const auth = useAuth();

	return (
		<MenuTrigger>
			<Button className="xl:hidden inline-flex gap-0.5 items-center px-3 py-1 rounded-3 inset-shadow-glow border bg-background cursor-pointer mr-4">
				<Dot className="w-7 shrink-0 p-1.75" />
				Menu
				<Chevron className="w-5 text-muted-foreground" />
			</Button>
			<Popover
				className={cx(
					"transition-opacity bg-background inset-shadow-glow/20 border border-glow py-3.5 pl-3 pr-5 rounded-xl",
					"data-[exiting]:opacity-0 data-[entering]:opacity-0",
				)}
				ref={submenuTriggerRef}
			>
				<AriaMenu className="grid gap-3">
					<MenuSection className="grid gap-3">
						<MenuItem href="/">
							<Home className="w-6" />
							Home
						</MenuItem>
						<MenuItem href="/settings">
							<Settings className="w-6" />
							Settings
						</MenuItem>
					</MenuSection>
					<MenuSection className="grid gap-3">
						<Header className="text-dark pl-3">Safety</Header>
						<MenuItem href="/policies">
							<Policies className="w-6" />
							Policies
						</MenuItem>
						<MenuItem href="/protections">
							<Protections className="w-6" />
							Protections
						</MenuItem>
						<MenuItem href="/deployments">
							<Deployments className="w-6" />
							Deployments
						</MenuItem>
					</MenuSection>

					<MenuSection className="border-t pt-3">
						<SubmenuTrigger>
							<AriaMenuItem className="flex items-center gap-2 w-full px-3 mt-auto">
								<Dot className="w-6 p-1" />
								{email}
								<Chevron className="ml-auto text-muted-foreground w-5 -rotate-90" />
							</AriaMenuItem>

							<Popover
								className={cx(
									"transition-opacity bg-background inset-shadow-glow/20 border border-glow py-3.5 pl-3 pr-5 rounded-xl",
									"data-[exiting]:opacity-0 data-[entering]:opacity-0",
								)}
								placement="end bottom"
								triggerRef={submenuTriggerRef}
							>
								<AriaMenu className="flex flex-col gap-2 w-fit">
									<MenuItem href="/settings/profile">
										<Profile className="w-7" />
										Profile
									</MenuItem>
									<MenuItem
										className="flex items-center gap-x-1.5 rounded-full cursor-pointer"
										href="/login"
										onAction={() => {
											auth.signOut().then(() => router.push("/login"));
										}}
									>
										<Logout className="w-7" />
										Log out
									</MenuItem>
								</AriaMenu>
							</Popover>
						</SubmenuTrigger>
					</MenuSection>
				</AriaMenu>
			</Popover>
		</MenuTrigger>
	);
}
